import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../utils/cn';
import ThreeJSOrb from './ThreeJSOrb';

const AIAgentDemo = () => {
  const navigate = useNavigate();
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState("");

  // Demo simulation
  const handleStartConversation = () => {
    setIsConnected(true);
    setCurrentTranscript("Hello! This is a demo of FLO. I'm here to help you with any questions about your medical procedures.");
    
    // Simulate speaking
    setTimeout(() => {
      setIsSpeaking(true);
      setTimeout(() => {
        setIsSpeaking(false);
      }, 3000);
    }, 1000);
  };

  const handleEndConversation = () => {
    setIsConnected(false);
    setIsSpeaking(false);
    setCurrentTranscript("");
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-white via-orange-50 to-orange-100 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 left-10 w-64 h-64 bg-orange-200/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-orange-300/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-100/40 rounded-full blur-3xl animate-ping" />
      </div>

      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 z-20 flex items-center space-x-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-orange-200 rounded-full hover:bg-white hover:border-orange-300 transition-all duration-300 shadow-lg hover:shadow-xl"
      >
        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="text-orange-700 font-medium">Back</span>
      </button>

      {/* Demo Banner */}
      <div className="absolute top-4 right-4 z-20 bg-amber-100/90 backdrop-blur-sm border border-amber-300 rounded-full px-4 py-2 shadow-lg">
        <span className="text-amber-700 font-medium text-sm">Demo Mode</span>
      </div>

      {/* Main Content Container */}
      <div className="flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="text-center mb-8 lg:mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 bg-clip-text text-transparent mb-4">
            FLO
          </h1>
          <p className="text-lg sm:text-xl text-orange-700/80 font-medium">
            Your AI Nurse Assistant - Demo
          </p>
        </div>

        {/* Three.js Orb Container */}
        <div className="relative mb-8 lg:mb-12">
          {/* Three.js Background Orb */}
          <div className="absolute inset-0 w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96">
            <ThreeJSOrb 
              isConnected={isConnected} 
              isSpeaking={isSpeaking} 
            />
          </div>

          {/* Interactive Orb Overlay */}
          <div className={cn(
            "relative w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-full backdrop-blur-sm border-2 transition-all duration-500 cursor-pointer flex items-center justify-center",
            isConnected
              ? isSpeaking
                ? "border-green-400/50 bg-green-50/20 shadow-2xl shadow-green-400/20 scale-105"
                : "border-orange-400/50 bg-orange-50/20 shadow-2xl shadow-orange-400/20"
              : "border-orange-200/50 bg-white/20 hover:border-orange-300/50 hover:bg-white/30 shadow-xl"
          )} 
          onClick={isConnected ? handleEndConversation : handleStartConversation}>
            
            {/* Center Content */}
            <div className="flex flex-col items-center space-y-4">
              {isConnected ? (
                <>
                  <div className={cn(
                    "w-12 h-12 sm:w-16 sm:h-16 rounded-full transition-all duration-300 flex items-center justify-center",
                    isSpeaking 
                      ? "bg-green-400 animate-pulse shadow-lg shadow-green-400/50" 
                      : "bg-orange-400 shadow-lg shadow-orange-400/50"
                  )}>
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                    </svg>
                  </div>
                  <div className="text-center">
                    <p className={cn(
                      "text-sm sm:text-base font-medium",
                      isSpeaking ? "text-green-600" : "text-orange-600"
                    )}>
                      {isSpeaking ? "AI Speaking..." : "Listening..."}
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 flex items-center justify-center shadow-xl shadow-orange-400/30 hover:shadow-orange-400/50 transition-all duration-300">
                    <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                    </svg>
                  </div>
                  <p className="text-orange-600 font-medium text-sm sm:text-base">
                    Tap to start demo
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Transcript Area */}
        {currentTranscript && (
          <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl mb-8 relative z-10">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl border border-orange-200 p-6 shadow-xl">
              <div className="text-center">
                <p className="text-orange-800 text-sm sm:text-base leading-relaxed">
                  {currentTranscript}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Welcome Message */}
        {!isConnected && !currentTranscript && (
          <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl mb-8 relative z-10">
            <div className="text-center">
              <p className="text-orange-700/80 text-sm sm:text-base lg:text-lg leading-relaxed">
                Experience FLO in demo mode. Get all your questions about &apos;your procedure&apos; answered 24/7.
              </p>
              <div className="mt-4 bg-amber-50/80 backdrop-blur-sm border border-amber-200 rounded-xl p-4">
                <p className="text-amber-700 text-xs sm:text-sm">
                  <strong>Note:</strong> This is a demo version. For full functionality with real AI responses, use the main AI Agent.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Indicators */}
        <div className="flex flex-col items-center space-y-4 w-full max-w-md relative z-10">
          {/* Connection Status */}
          {isConnected && (
            <div className="flex items-center space-x-3 bg-green-50/80 backdrop-blur-sm border border-green-200 rounded-full px-6 py-3 shadow-lg">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
              <span className="text-sm sm:text-base text-green-700 font-medium">Demo Connected</span>
              {isSpeaking && (
                <span className="text-sm text-green-600">• AI Speaking</span>
              )}
            </div>
          )}

          {/* Connection Indicator */}
          <div className="flex items-center space-x-3">
            <div className={cn(
              "w-2 h-2 rounded-full transition-colors",
              isConnected 
                ? "bg-green-400 shadow-lg shadow-green-400/50" 
                : "bg-orange-400 shadow-lg shadow-orange-400/50"
            )}></div>
            <span className="text-sm text-orange-600/80">
              {isConnected ? "Demo Active" : "Ready for demo"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAgentDemo;
